/**
 * EPR material pricing, FAQ copy and JSON-LD for /epr.
 *
 * Kept out of EPR.tsx so `src/routes/_app/epr.tsx` can build head metadata
 * without bundling the page (hero image, estimator, enquiry form).
 */
const SITE = "https://lohalink.in";
const CANONICAL = `${SITE}/epr`;

/** Indicative EPR credit brokerage rates (₹ per tonne). Directional only. */
export const MATERIALS = [
  {
    key: "plastic",
    label: "Plastic Waste (Cat I–IV)",
    rule: "Plastic Waste Management Rules, 2016 (as amended)",
    low: 4000,
    high: 12000,
    note: "Rigid, flexible, multilayered and compostable categories are priced separately; Category II & III carry the tightest supply.",
  },
  {
    key: "ewaste",
    label: "E-Waste (EEE Codes)",
    rule: "E-Waste (Management) Rules, 2022",
    low: 12000,
    high: 32000,
    note: "Priced per EEE code. Floor pricing and environmental compensation make early-year procurement materially cheaper.",
  },
  {
    key: "battery",
    label: "Battery Waste",
    rule: "Battery Waste Management Rules, 2022",
    low: 9000,
    high: 26000,
    note: "Lead-acid, Li-ion, nickel-cadmium and other chemistries have separate recovery targets and credit pools.",
  },
  {
    key: "tyre",
    label: "Waste Tyre",
    rule: "Hazardous & Other Wastes (M&TM) Rules — Tyre EPR",
    low: 3500,
    high: 9000,
    note: "Credits generated by registered recyclers producing crumb rubber, reclaimed rubber, char or pyro oil.",
  },
  {
    key: "usedoil",
    label: "Used / Waste Oil",
    rule: "Hazardous & Other Wastes (M&TM) Rules — Used Oil EPR",
    low: 3000,
    high: 8000,
    note: "Sourced through CPCB-registered re-refiners with verified mass-balance records.",
  },
  {
    key: "metal",
    label: "Metal & Ferrous Recycling",
    rule: "Steel Scrap Recycling Policy alignment",
    low: 1200,
    high: 4500,
    note: "For producers reporting recycled content and Scope 3 circularity alongside EPR obligations.",
  },
] as const;

export type MaterialKey = (typeof MATERIALS)[number]["key"];

export const FAQS = [
  {
    q: "What is EPR and who is legally liable in India?",
    a: "Extended Producer Responsibility (EPR) makes the Producer, Importer or Brand Owner (PIBO) legally responsible for the end-of-life management of the products and packaging they place on the Indian market. Liability sits with the PIBO — not with the recycler, distributor or waste aggregator — and is enforced by the CPCB through the EPR portal, annual returns and Environmental Compensation.",
  },
  {
    q: "What is an EPR credit or EPR certificate?",
    a: "When a registered recycler processes a tonne of waste, it generates an EPR certificate on the CPCB portal for that category. A PIBO discharges its target by acquiring and having those certificates transferred to its portal account. LohaLink brokers that transaction end to end — sourcing, verification, transfer and reconciliation.",
  },
  {
    q: "How much does EPR compliance cost per tonne?",
    a: "It varies by waste stream, category and how late in the financial year you buy. Indicatively, plastic waste credits trade around ₹4,000–₹12,000 per tonne, e-waste ₹12,000–₹32,000 per tonne, battery waste ₹9,000–₹26,000 per tonne, tyre ₹3,500–₹9,000 per tonne and used oil ₹3,000–₹8,000 per tonne. Use the estimator on this page for a directional multi-material budget, then request a firm written quote.",
  },
  {
    q: "What happens if I miss my EPR target?",
    a: "Shortfalls attract Environmental Compensation, which is typically a multiple of the prevailing credit price, plus a carry-forward of the unmet obligation into the following years. Portal accounts can be suspended and consent to operate can be affected — which is why obligations should be closed progressively, not in March.",
  },
  {
    q: "How does LohaLink prevent fake or double-counted credits?",
    a: "Every recycler in our pool is validated against its CPCB/SPCB registration, category authorisation and installed capacity. Certificates are matched line-by-line to your declared category and financial year, and payment releases from escrow only after the transfer reflects in your own portal account.",
  },
  {
    q: "Can LohaLink handle multi-material obligations in one contract?",
    a: "Yes. Most of our clients carry plastic packaging plus e-waste or battery liability. We consolidate the entire basket into one obligation model, one procurement calendar, one escrow contract and one reconciliation pack for your auditors.",
  },
  {
    q: "Do you support ESG, BRSR and Scope 3 reporting?",
    a: "We provide a documentation pack — recycler registrations, invoices, weighbridge and processing records, certificate trails and category-wise tonnage — formatted for BRSR disclosure, ESG assurance and internal circularity reporting.",
  },
  {
    q: "How long does onboarding take?",
    a: "A typical PIBO goes from first call to signed obligation plan within 5–7 working days once quantities placed on the market and portal registration details are shared. Credit sourcing then runs on the agreed calendar across the financial year.",
  },
];

export const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQS.map((f) => ({
    "@type": "Question",
    name: f.q,
    acceptedAnswer: { "@type": "Answer", text: f.a },
  })),
};

export const serviceLd = {
  "@context": "https://schema.org",
  "@type": "Service",
  name: "EPR Credit Brokerage & Compliance Management",
  serviceType: "Extended Producer Responsibility (EPR) brokerage",
  description:
    "LohaLink brokers EPR credits for plastic waste, e-waste, battery waste, tyre and used oil in India — sourcing certificates from CPCB-registered recyclers with escrow-protected settlement and audit-ready documentation.",
  url: CANONICAL,
  areaServed: { "@type": "Country", name: "India" },
  provider: {
    "@type": "Organization",
    name: "LohaLink",
    url: SITE,
    logo: { "@type": "ImageObject", url: `${SITE}/logo.png` },
  },
  audience: {
    "@type": "BusinessAudience",
    name: "Producers, Importers and Brand Owners (PIBOs)",
  },
  hasOfferCatalog: {
    "@type": "OfferCatalog",
    name: "EPR waste streams brokered",
    itemListElement: MATERIALS.map((m) => ({
      "@type": "Offer",
      itemOffered: { "@type": "Service", name: m.label },
      priceSpecification: {
        "@type": "PriceSpecification",
        priceCurrency: "INR",
        minPrice: m.low,
        maxPrice: m.high,
        unitText: "per tonne",
      },
    })),
  },
};

export const breadcrumbLd = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: `${SITE}/` },
    { "@type": "ListItem", position: 2, name: "EPR Credit Brokerage", item: CANONICAL },
  ],
};
